import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import moment from "moment";

// English is bundled (it is the fallback for any missing text).
// Every other language is loaded on demand from ./locales/<code>/<namespace>.json
// as its own small chunk, so adding languages doesn't grow the main bundle.
import en from "./locales/en/common.json";
import enClientBooking from "./locales/en/clientBooking.json";
import enClientPremium from "./locales/en/clientPremium.json";
import enClientBookLater from "./locales/en/clientBookLater.json";
import enClientSessions from "./locales/en/clientSessions.json";
import enClientOnDemand from "./locales/en/clientOnDemand.json";
import enClientLive from "./locales/en/clientLive.json";
import enClientProfile from "./locales/en/clientProfile.json";
import enClientMisc from "./locales/en/clientMisc.json";
import enCheckIn from "./locales/en/checkIn.json";
import enComponents from "./locales/en/components.json";
import enMediumAppointments from "./locales/en/mediumAppointments.json";
import enMediumBooking from "./locales/en/mediumBooking.json";
import enMediumMisc from "./locales/en/mediumMisc.json";
import enMediumOverview from "./locales/en/mediumOverview.json";
import enMediumFeedbacks from "./locales/en/mediumFeedbacks.json";
import enMediumPractice1 from "./locales/en/mediumPractice1.json";
import enMediumPractice2 from "./locales/en/mediumPractice2.json";
import enMediumPracticePopups from "./locales/en/mediumPracticePopups.json";
import enMediumSchedule from "./locales/en/mediumSchedule.json";
import enMediumAvailability from "./locales/en/mediumAvailability.json";
import enMediumPricing from "./locales/en/mediumPricing.json";
import enMediumBackfill from "./locales/en/mediumBackfill.json";
import enMediumIntegration from "./locales/en/mediumIntegration.json";
import enMediumProfile from "./locales/en/mediumProfile.json";
import enMediumRegister from "./locales/en/mediumRegister.json";
import enMediumRegisterSteps from "./locales/en/mediumRegisterSteps.json";
import enAuthLogin from "./locales/en/authLogin.json";
import enAuthRegister from "./locales/en/authRegister.json";
import enLegalTerms from "./locales/en/legalTerms.json";
import enLegalPrivacy from "./locales/en/legalPrivacy.json";
import enPublicGuestBooking from "./locales/en/publicGuestBooking.json";
import enPublicInterviewBooking from "./locales/en/publicInterviewBooking.json";
import enPublicMisc from "./locales/en/publicMisc.json";
import enDynamicContent from "./locales/en/dynamicContent.json";
import enDynamicWords from "./locales/en/dynamicWords.json";

// One JSON file per area of the app ("namespace"). Use them with
// useTranslation("clientBooking") etc.; "common" is the default.
export const NAMESPACES = [
  "common",
  "clientBooking",
  "clientPremium",
  "clientBookLater",
  "clientSessions",
  "clientOnDemand",
  "clientLive",
  "clientProfile",
  "clientMisc",
  "checkIn",
  "components",
  "mediumAppointments",
  "mediumBooking",
  "mediumMisc",
  "mediumOverview",
  "mediumFeedbacks",
  "mediumPractice1",
  "mediumPractice2",
  "mediumPracticePopups",
  "mediumSchedule",
  "mediumAvailability",
  "mediumPricing",
  "mediumBackfill",
  "mediumIntegration",
  "mediumProfile",
  "mediumRegister",
  "mediumRegisterSteps",
  "authLogin",
  "authRegister",
  "legalTerms",
  "legalPrivacy",
  "publicGuestBooking",
  "publicInterviewBooking",
  "publicMisc",
  "dynamicContent", // text that comes from the API, see ./dynamicContent.js
  "dynamicWords", // common single words, last-resort fallback for dynamicContent
];

const EN_RESOURCES = {
  common: en,
  clientBooking: enClientBooking,
  clientPremium: enClientPremium,
  clientBookLater: enClientBookLater,
  clientSessions: enClientSessions,
  clientOnDemand: enClientOnDemand,
  clientLive: enClientLive,
  clientProfile: enClientProfile,
  clientMisc: enClientMisc,
  checkIn: enCheckIn,
  components: enComponents,
  mediumAppointments: enMediumAppointments,
  mediumBooking: enMediumBooking,
  mediumMisc: enMediumMisc,
  mediumOverview: enMediumOverview,
  mediumFeedbacks: enMediumFeedbacks,
  mediumPractice1: enMediumPractice1,
  mediumPractice2: enMediumPractice2,
  mediumPracticePopups: enMediumPracticePopups,
  mediumSchedule: enMediumSchedule,
  mediumAvailability: enMediumAvailability,
  mediumPricing: enMediumPricing,
  mediumBackfill: enMediumBackfill,
  mediumIntegration: enMediumIntegration,
  mediumProfile: enMediumProfile,
  mediumRegister: enMediumRegister,
  mediumRegisterSteps: enMediumRegisterSteps,
  authLogin: enAuthLogin,
  authRegister: enAuthRegister,
  legalTerms: enLegalTerms,
  legalPrivacy: enLegalPrivacy,
  publicGuestBooking: enPublicGuestBooking,
  publicInterviewBooking: enPublicInterviewBooking,
  publicMisc: enPublicMisc,
  dynamicContent: enDynamicContent,
  dynamicWords: enDynamicWords,
};

// Storage key (localStorage + cookie) holding the chosen language.
export const LANGUAGE_STORAGE_KEY = "i18nextLng";

// Mirrors the backend `languages` table. `code` is the i18next code (DB codes
// normalised: zh_CN -> zh-CN, iw -> he). `moment` is moment.js's locale name
// where it differs from `code`. Not included: Haryanvi (no standard written
// UI language), Tagalog (same as Filipino "tl"), ASL (sign language).
export const LANGUAGES = [
  { code: "ar", label: "العربية", english: "Arabic", rtl: true },
  { code: "bn", label: "বাংলা", english: "Bengali" },
  { code: "bg", label: "Български", english: "Bulgarian" },
  { code: "ca", label: "Català", english: "Catalan" },
  { code: "zh-CN", label: "简体中文", english: "Chinese (simplified)", moment: "zh-cn" },
  { code: "zh-TW", label: "繁體中文", english: "Chinese (traditional)", moment: "zh-tw" },
  { code: "hr", label: "Hrvatski", english: "Croatian" },
  { code: "cs", label: "Čeština", english: "Czech" },
  { code: "da", label: "Dansk", english: "Danish" },
  { code: "nl", label: "Nederlands", english: "Dutch" },
  { code: "en", label: "English", english: "English" },
  { code: "et", label: "Eesti", english: "Estonian" },
  { code: "tl", label: "Filipino", english: "Filipino", moment: "tl-ph" },
  { code: "fi", label: "Suomi", english: "Finnish" },
  { code: "fr", label: "Français", english: "French" },
  { code: "de", label: "Deutsch", english: "German" },
  { code: "el", label: "Ελληνικά", english: "Greek" },
  { code: "gu", label: "ગુજરાતી", english: "Gujarati" },
  { code: "he", label: "עברית", english: "Hebrew", rtl: true },
  { code: "hi", label: "हिन्दी", english: "Hindi" },
  { code: "hu", label: "Magyar", english: "Hungarian" },
  { code: "is", label: "Íslenska", english: "Icelandic" },
  { code: "id", label: "Bahasa Indonesia", english: "Indonesian" },
  { code: "it", label: "Italiano", english: "Italian" },
  { code: "ja", label: "日本語", english: "Japanese" },
  { code: "kn", label: "ಕನ್ನಡ", english: "Kannada" },
  { code: "ko", label: "한국어", english: "Korean" },
  { code: "lv", label: "Latviešu", english: "Latvian" },
  { code: "lt", label: "Lietuvių", english: "Lithuanian" },
  { code: "ms", label: "Bahasa Melayu", english: "Malay" },
  { code: "ml", label: "മലയാളം", english: "Malayalam" },
  { code: "mr", label: "मराठी", english: "Marathi" },
  { code: "no", label: "Norsk", english: "Norwegian", moment: "nb" },
  { code: "fa", label: "فارسی", english: "Persian", rtl: true },
  { code: "pl", label: "Polski", english: "Polish" },
  { code: "pt", label: "Português", english: "Portuguese" },
  { code: "pa", label: "ਪੰਜਾਬੀ", english: "Punjabi", moment: "pa-in" },
  { code: "ro", label: "Română", english: "Romanian" },
  { code: "ru", label: "Русский", english: "Russian" },
  { code: "sr", label: "Српски", english: "Serbian" },
  { code: "sk", label: "Slovenčina", english: "Slovak" },
  { code: "sl", label: "Slovenščina", english: "Slovenian" },
  { code: "es", label: "Español", english: "Spanish" },
  { code: "sv", label: "Svenska", english: "Swedish" },
  { code: "ta", label: "தமிழ்", english: "Tamil" },
  { code: "te", label: "తెలుగు", english: "Telugu" },
  { code: "th", label: "ไทย", english: "Thai" },
  { code: "tr", label: "Türkçe", english: "Turkish" },
  { code: "uk", label: "Українська", english: "Ukrainian" },
  { code: "ur", label: "اردو", english: "Urdu", rtl: true },
  { code: "vi", label: "Tiếng Việt", english: "Vietnamese" },
];

const SUPPORTED = LANGUAGES.map((l) => l.code);
const ALIASES = { iw: "he", nb: "no", nn: "no", fil: "tl", in: "id" };

// Map whatever the browser / DB / old storage says (pt-BR, zh_CN, iw, ...)
// onto one of our supported codes.
export const normalizeLanguage = (lng) => {
  if (!lng) return "en";
  const lower = String(lng).replace("_", "-").toLowerCase();
  if (lower.startsWith("zh")) {
    return /-(tw|hk|mo|hant)/.test(lower) ? "zh-TW" : "zh-CN";
  }
  const base = lower.split("-")[0];
  const code = ALIASES[base] || base;
  return SUPPORTED.includes(code) ? code : "en";
};

const lazyLocaleBackend = {
  type: "backend",
  init() {},
  read(lng, ns, callback) {
    if (lng === "en") return callback(null, EN_RESOURCES[ns] || {});
    import(/* webpackChunkName: "locale-[request]" */ `./locales/${lng}/${ns}.json`)
      .then((mod) => callback(null, mod.default || mod))
      .catch((err) => callback(err, null));
  },
};

i18n
  .use(lazyLocaleBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: EN_RESOURCES },
    partialBundledLanguages: true, // en bundled, the rest via the backend
    ns: NAMESPACES, // all namespaces load up front, so pages never flash keys
    defaultNS: "common",
    fallbackLng: "en",
    supportedLngs: SUPPORTED,
    load: "currentOnly", // don't also try "zh" for "zh-CN"
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      // The cookie is a backup: many places call localStorage.clear()
      // (logout, token expiry, redirects), and the cookie survives that.
      // No "navigator": everyone starts in English until they pick a language.
      order: ["localStorage", "cookie"],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      lookupCookie: LANGUAGE_STORAGE_KEY,
      cookieMinutes: 60 * 24 * 365,
      caches: ["localStorage", "cookie"],
      convertDetectedLanguage: normalizeLanguage,
    },
    react: { useSuspense: false },
  });

// Keep <html lang/dir> in sync with the language.
// moment's global locale is deliberately pinned to English: moment builds the
// date/time strings sent to the API (e.g. format("YYYY-MM-DD")) in ~150 files,
// and a non-English global locale turns those into native digits / month names
// (e.g. "२०२६-१०-०९"), breaking API payloads. Translation is display-only.
const syncLanguage = (lng) => {
  const code = normalizeLanguage(lng);
  const meta = LANGUAGES.find((l) => l.code === code) || {};
  document.documentElement.lang = code;
  document.documentElement.dir = meta.rtl ? "rtl" : "ltr";
  moment.locale("en");
};
syncLanguage(i18n.language);
i18n.on("languageChanged", syncLanguage);

export default i18n;
